import { useState } from "react";
import type { SubmitEvent } from "react";
import { BackLink, Button, Checkbox, Container, ContentPanel, ErrorSummary, LabelledValue, PortalLayout, PortalPanelBody, Stack, Stepper, TextField, Typography } from "../../design-system";
import type { StepperItemData } from "../../design-system";
import { DECLARATION_REPRESENTATIVE_NAME_MAX_LENGTH, getErrorMessage } from "../../validation/error-registry";
import { buildSaveOutcomeContent } from "../renewal-capture/outcome-content";
import { declarationContent as content } from "./content";

/**
 * Declaration route (H2 Screen 6), rewritten for TG-6 (AC-20, AC-21).
 * Presentational: no router import, no design-system-external I/O, no
 * step-state computation of its own — `stepperItems` is computed by the
 * composition root (App.tsx's `DeclarationRouteWrapper`) from the SAME
 * `computeStepperStages` every other stepper in this feature already
 * uses (RenewalLanding's own), never a second, hand-rolled stepper
 * mechanism.
 *
 * `todayDisplay` (the static date text, "Date: October 13, 2026") is
 * supplied by the caller rather than computed with `new Date()` here —
 * this component stays a pure function of its props, and a caller's own
 * test can supply a frozen date deterministically (AC-20's own worked
 * example: "Given an owner on screen 6 on 2026-10-13").
 *
 * Submit is always activatable (FIND-24/AC-10, carried from the retired
 * H1 copy of this file): never a `disabled` attribute — the form's own
 * `onSubmit` handler validates on every attempt and names every missing
 * field individually (AC-20), and `visuallyDisabled` gives the "looks
 * disabled while invalid" treatment without removing the control from the
 * tab order.
 */
export interface DeclarationSubmission {
  representativeName: string;
  /** TG-4 (m1-declaration-next-steps): the checkbox's own validated value at the moment `onSubmit`
   * fires -- always `true` (the form's own `isValid` check already requires it before `onSubmit` is
   * ever called), carried explicitly rather than assumed by the caller so `attemptSubmit` (App.tsx)
   * has a single, named value to forward as the real endpoint's own `agreed` field, never a literal
   * `true` typed a second time at the call site. */
  agreed: boolean;
}

/** TG-4: the two fields screen 6's own 422 `invalid_declaration` refusal can name (THR-4,
 * `src/api/functions/submission.py`'s own validated set) -- supplied by the caller
 * (`DeclarationRouteWrapper`) after mapping the server's `fieldErrors` onto `ERROR_REGISTRY`
 * strings, one per server reason for the name (REQ-004). Shown in
 * ADDITION to local validation, for the case a server-side check catches something the client's own
 * `computeErrors` does not (a race, or a field the client never validates at all, e.g. control
 * characters) -- the client's own error, when both exist for the same field, always wins, since a
 * fresh edit already makes the server's stale claim moot. */
export interface DeclarationServerFieldErrors {
  representativeName?: string;
  acknowledgement?: string;
}

export interface DeclarationRouteProps {
  readonly businessName: string;
  readonly todayDisplay: string;
  readonly stepperItems: readonly StepperItemData[];
  readonly onSubmit: (submission: DeclarationSubmission) => void;
  readonly onSaveAndFinishLater: () => void;
  readonly onBack?: () => void;
  /** AC-21: set when the previous submission attempt returned Failed or
   * Indeterminate — the customer's entries stay on screen and the rendered
   * panel states plainly that the renewal was not submitted (never "Thank
   * you for submitting" on any screen in that case). m1-portal-ux-fixes
   * Fix C: carries WHICH of the two outcomes, not just a boolean, so the
   * two distinct, Content-Design-authored messages
   * (`../renewal-capture/outcome-content.ts`) render correctly; `undefined`
   * (the ordinary case) renders no panel. */
  readonly notSubmitted?: "Failed" | "Indeterminate";
  readonly isSubmitting?: boolean;
  /** TG-4 (AC: "422 invalid_declaration shows the field errors in screen 6's error summary"; WCAG
   * 2.2 SC 3.3.1/3.3.3). A FRESH object from the caller (a new submit attempt's own 422 response) is
   * what re-triggers the error summary and its own focus move — see this component's own
   * `serverErrorsKey` effect below. */
  readonly serverFieldErrors?: DeclarationServerFieldErrors | undefined;
}

interface DeclarationErrors {
  representativeName?: string | undefined;
  acknowledgement?: string | undefined;
}

function computeErrors(representativeName: string, acknowledged: boolean): DeclarationErrors {
  const errors: DeclarationErrors = {};
  const trimmedName = representativeName.trim();
  if (trimmedName === "") {
    errors.representativeName = getErrorMessage("declaration.representativeName", "missing");
  } else if ([...trimmedName].length > DECLARATION_REPRESENTATIVE_NAME_MAX_LENGTH) {
    // No `maxlength` on the input: it silently truncates a pasted name (GOV.UK Design System).
    // Counted in code points after trimming, as the server's `len(trimmed_name)` counts.
    errors.representativeName = getErrorMessage("declaration.representativeName", "tooLong");
  }
  if (!acknowledged) {
    errors.acknowledgement = getErrorMessage("declaration.acknowledgement", "missing");
  }
  return errors;
}

interface DeclarationFieldDisplay {
  readonly errors: DeclarationErrors;
  readonly isValid: boolean;
  readonly showErrors: boolean;
  readonly acknowledgementHelp: string | undefined;
  readonly representativeNameErrorText: string | undefined;
}

/** ADR-20261022-cgx7: the client/server error-merge and show/hide derivation this component used to
 * carry inline, pulled out to a standalone function so its branching is assessed on its own rather
 * than folded into `DeclarationRoute`'s cognitive complexity. Same precedence (a fresh client error
 * always wins over a stale server one), same `showErrors` condition, no behaviour change. */
function deriveDeclarationFieldDisplay(
  representativeName: string,
  acknowledged: boolean,
  submitAttempted: boolean,
  serverFieldErrors: DeclarationServerFieldErrors | undefined,
  dismissedServerErrors: { representativeName: boolean; acknowledgement: boolean },
): DeclarationFieldDisplay {
  const clientErrors = computeErrors(representativeName, acknowledged);
  const activeServerErrors: DeclarationErrors = {
    representativeName: dismissedServerErrors.representativeName ? undefined : serverFieldErrors?.representativeName,
    acknowledgement: dismissedServerErrors.acknowledgement ? undefined : serverFieldErrors?.acknowledgement,
  };
  const errors: DeclarationErrors = {
    representativeName: clientErrors.representativeName ?? activeServerErrors.representativeName,
    acknowledgement: clientErrors.acknowledgement ?? activeServerErrors.acknowledgement,
  };
  const hasServerErrors =
    activeServerErrors.representativeName !== undefined || activeServerErrors.acknowledgement !== undefined;
  const isValid = errors.representativeName === undefined && errors.acknowledgement === undefined;
  const showErrors = submitAttempted || hasServerErrors;
  return {
    errors,
    isValid,
    showErrors,
    acknowledgementHelp: showErrors ? errors.acknowledgement : undefined,
    representativeNameErrorText: showErrors ? errors.representativeName : undefined,
  };
}

/** ADR-20261022-cgx7: the error-summary item list, pulled out of the JSX so the two
 * presence-checks it used to run as inline spread-ternaries are assessed on their own. Same two
 * fields, same order, no behaviour change. */
function buildDeclarationErrorSummaryItems(errors: DeclarationErrors): { fieldId: string; text: string }[] {
  const items: { fieldId: string; text: string }[] = [];
  if (errors.representativeName !== undefined) {
    items.push({ fieldId: "declaration-representative-name", text: errors.representativeName });
  }
  if (errors.acknowledgement !== undefined) {
    items.push({ fieldId: "declaration-acknowledgement", text: errors.acknowledgement });
  }
  return items;
}

export function DeclarationRoute({
  businessName,
  todayDisplay,
  stepperItems,
  onSubmit,
  onSaveAndFinishLater,
  onBack,
  notSubmitted,
  isSubmitting = false,
  serverFieldErrors,
}: Readonly<DeclarationRouteProps>) {
  const [representativeName, setRepresentativeName] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // TG-4: a server field error is shown until the customer edits the field it names (a fresh local
  // edit makes a stale server claim moot) OR until the caller supplies a DIFFERENT server error set
  // (a new submit attempt) -- reset "during render" (React's own documented pattern for adjusting
  // state when a prop changes: https://react.dev/learn/you-might-not-need-an-effect), never inside
  // a `useEffect` body, which would cause an extra, avoidable render on every change.
  const [dismissedServerErrors, setDismissedServerErrors] = useState({
    representativeName: false,
    acknowledgement: false,
  });
  const serverErrorsKey = `${serverFieldErrors?.representativeName ?? ""}|${serverFieldErrors?.acknowledgement ?? ""}`;
  const [lastServerErrorsKey, setLastServerErrorsKey] = useState(serverErrorsKey);
  if (serverErrorsKey !== lastServerErrorsKey) {
    setLastServerErrorsKey(serverErrorsKey);
    setDismissedServerErrors({ representativeName: false, acknowledgement: false });
  }

  const { errors, isValid, showErrors, acknowledgementHelp, representativeNameErrorText } = deriveDeclarationFieldDisplay(
    representativeName,
    acknowledged,
    submitAttempted,
    serverFieldErrors,
    dismissedServerErrors,
  );

  function handleRepresentativeNameChange(value: string) {
    setRepresentativeName(value);
    setDismissedServerErrors((previous) => ({ ...previous, representativeName: true }));
  }

  function handleAcknowledgedChange(value: boolean) {
    setAcknowledged(value);
    setDismissedServerErrors((previous) => ({ ...previous, acknowledgement: true }));
  }

  function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitAttempted(true);
    if (!isValid) {
      return;
    }
    onSubmit({ representativeName: representativeName.trim(), agreed: acknowledged });
  }

  // m1-portal-ux-fixes Fix C: computed once per render, named by THIS
  // screen's own primary action ("Submit renewal") -- undefined when there
  // is no outcome to show, in which case nothing below reads it.
  const notSubmittedOutcome = notSubmitted !== undefined ? buildSaveOutcomeContent(content.submitLabel)[notSubmitted] : undefined;

  return (
    <PortalLayout businessName={businessName}>
      <Container layout="exposures">
        <>
          <Stepper aria-label="Renewal steps" items={stepperItems} testId="declaration-stepper" />

          {/* H2 (06-Declaration.dc.html, README.md "Back link -- above the
              card"): the back link and the eyebrow/heading/intro group sit
              above the card, never inside it -- unlike screen 3's own
              heading, which H2 nests inside its card. */}
          {onBack !== undefined ? <BackLink label={content.backLabel} onClick={onBack} compact testId="declaration-back" /> : null}

          <Stack gap="heading">
            <Typography variant="eyebrow" as="p">
              {content.eyebrow}
            </Typography>
            <Typography variant="display" as="h1">
              {content.heading}
            </Typography>
            <Typography variant="bodyLarge">{content.intro}</Typography>
          </Stack>

          <ContentPanel variant="declaration">
            {/* Fix B (m1-portal-ux-fixes): the save-outcome panel renders
                inside the card, at the top, alongside where the error
                summary below renders -- never above/outside the card as the
                design-system Alert this replaces used to. */}
            {notSubmittedOutcome !== undefined ? (
              <PortalPanelBody
                id="declaration-not-submitted"
                expanded
                tone="error"
                title={notSubmittedOutcome.title}
                testId="declaration-not-submitted"
              >
                <Typography variant="body">{notSubmittedOutcome.message}</Typography>
              </PortalPanelBody>
            ) : null}

            {showErrors && !isValid ? (
              <ErrorSummary id="declaration-errors" items={buildDeclarationErrorSummaryItems(errors)} />
            ) : null}

            <form onSubmit={handleSubmit} noValidate>
              <Stack gap="lg">
                <Stack gap="sm">
                  <Typography variant="heading3" as="h2">
                    {content.statementHeading}
                  </Typography>
                  <Typography variant="body">{content.statement}</Typography>

                  <Checkbox
                    id="declaration-acknowledgement"
                    label={content.acknowledgementLabel}
                    checked={acknowledged}
                    onChange={handleAcknowledgedChange}
                    variant="plain"
                    error={acknowledgementHelp !== undefined}
                    {...(acknowledgementHelp !== undefined ? { help: acknowledgementHelp } : {})}
                  />
                </Stack>

                <Stack gap="xs">
                  <TextField
                    id="declaration-representative-name"
                    label={content.representativeNameLabel}
                    help={content.representativeNameHelp}
                    value={representativeName}
                    onChange={handleRepresentativeNameChange}
                    autoComplete="name"
                    error={representativeNameErrorText !== undefined}
                    {...(representativeNameErrorText !== undefined ? { errorText: representativeNameErrorText } : {})}
                  />

                  {/* H2 screen 6: "Date:" in bold beside the date, pulled up under the field. */}
                  <LabelledValue label={content.dateLabel} value={todayDisplay} underField testId="declaration-date" />
                </Stack>

                <Stack gap="sm">
                  <Typography variant="heading3" as="h2">
                    {content.disclosureHeading}
                  </Typography>
                  {/* FIX-2 (m1-portal-design-fixes): H2 (06-Declaration.dc.html:94,
                      PortalPanel.dc.html) renders the compensation disclosure always
                      visible inside the grey help-panel surface -- never behind a
                      toggle, unlike the data-on-file disclosure panels `PortalPanel`
                      itself serves. `PortalPanelBody` rendered with `expanded`
                      hardcoded `true` gives that exact surface (`#f4f7f9`-equivalent
                      background, 4px left rule, square corners) with no trigger and
                      no collapsed state at all. */}
                  <PortalPanelBody
                    id="compensation-disclosure"
                    expanded
                    paragraphs={content.disclosureParagraphs}
                    testId="declaration-compensation-disclosure"
                  />
                </Stack>

                <Stack direction="actions" gap="sm">
                  {/* ADR-20261016-v6nd (AC-2/AC-3's "declaration submit never
                      disabled"): `disabled={isSubmitting}` set the real HTML
                      `disabled` attribute while a submission was in flight --
                      contradicting this file's own header comment ("Submit is
                      always activatable ... never a disabled attribute") and
                      AC-3 ("No action button on any screen is ever disabled").
                      `visuallyDisabled` gives the identical look-disabled
                      treatment without removing the control from the tab
                      order or blocking the click the form's own `onSubmit`
                      handler still needs to run. */}
                  {/* H2 never greys an action (DESIGN-SYSTEM-DEPARTURES.md, "Buttons are never
                      disabled"): an incomplete form answers Submit with the error summary. */}
                  <Button type="submit" emphasis="primary" size="large" testId="declaration-submit" visuallyDisabled={isSubmitting}>
                    {content.submitLabel}
                  </Button>
                  <Button type="button" emphasis="quiet" size="large" onClick={onSaveAndFinishLater} visuallyDisabled={isSubmitting}>
                    {content.saveAndFinishLaterLabel}
                  </Button>
                </Stack>
                {/* H2 screen 6: the help line closes the card, under the actions. */}
                <Typography variant="bodySmall" tone="muted">
                  {content.helpLine}
                </Typography>
              </Stack>
            </form>
          </ContentPanel>
        </>
      </Container>
    </PortalLayout>
  );
}
